/**
 * AP-PAY-01C: Finance Credit Note Workspace
 * Route: /ap/credit-notes (ap.invoice.view)
 *
 * Lists credit notes, allows creation, submission, approval and rejection.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, RefreshCw, ChevronRight, Check, X, FileText, AlertTriangle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

function fmt(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency,
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(minor / 100);
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

const CN_STATUS_CFG = {
  DRAFT:      { label: "Draft",      bg: "#F5F5F5", text: "#666",    border: "#DDD" },
  SUBMITTED:  { label: "Submitted",  bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE" },
  APPROVED:   { label: "Approved",   bg: "#F0FDF4", text: "#166534", border: "#BBF7D0" },
  REJECTED:   { label: "Rejected",   bg: "#FEF2F2", text: "#991B1B", border: "#FECACA" },
  VOIDED:     { label: "Voided",     bg: "#F5F5F5", text: "#9CA3AF", border: "#E5E7EB" },
};

function CNStatusBadge({ status }) {
  const c = CN_STATUS_CFG[status] || { label: status, bg: "#F5F5F5", text: "#666", border: "#DDD" };
  return (
    <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: "20px",
      fontSize: "11px", fontWeight: 700, background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>
      {c.label}
    </span>
  );
}

const ALL_STATUSES = ["DRAFT", "SUBMITTED", "APPROVED", "REJECTED", "VOIDED"];

export default function FinanceCreditNotes() {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [items,       setItems]       = useState([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [loading,     setLoading]     = useState(false);
  const [activeFilter, setActiveFilter] = useState("ALL");
  const [submitting,  setSubmitting]  = useState(false);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [approvedInvoices, setApprovedInvoices] = useState([]);
  const [createForm, setCreateForm] = useState({
    original_invoice_id: "", supplier_credit_note_number: "",
    credit_note_date: new Date().toISOString().slice(0, 10),
    reason: "", currency_code: "INR",
    subtotal_minor: "", tax_adjustment_minor: "0", notes: "",
  });

  // Approve modal
  const [approveCN,  setApproveCN]  = useState(null);
  const [approveAmt, setApproveAmt] = useState("");

  const hasCap     = (cap) => user?.capabilities?.includes(cap) || user?.capabilities?.includes("ap.*");
  const canValidate = hasCap("ap.invoice.validate");
  const canApprove  = hasCap("ap.invoice.approve");
  const PAGE_SIZE   = 20;

  const loadCNs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (activeFilter !== "ALL") params.status = activeFilter;
      const res = await axios.get(`${API}/api/ap/credit-notes`, { params, withCredentials: true });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load credit notes");
    } finally {
      setLoading(false);
    }
  }, [page, activeFilter]);

  useEffect(() => { loadCNs(); }, [loadCNs]);

  const loadApprovedInvoices = useCallback(async () => {
    if (!showCreate) return;
    try {
      const res = await axios.get(`${API}/api/ap/match/queue`, {
        params: { status: "APPROVED", page_size: 50 }, withCredentials: true,
      });
      setApprovedInvoices(res.data.items || []);
    } catch { /* non-blocking */ }
  }, [showCreate]);

  useEffect(() => { loadApprovedInvoices(); }, [loadApprovedInvoices]);

  // Create CN
  const submitCreate = async (e) => {
    e.preventDefault();
    const subtotal = parseInt(createForm.subtotal_minor, 10);
    const taxAdj   = parseInt(createForm.tax_adjustment_minor, 10) || 0;
    if (!subtotal || subtotal <= 0) return toast.error("Subtotal must be positive (minor units)");

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/ap/credit-notes`, {
        ...createForm,
        subtotal_minor: subtotal,
        tax_adjustment_minor: taxAdj,
      }, { withCredentials: true });
      toast.success("Credit note created");
      setShowCreate(false);
      setCreateForm({
        original_invoice_id: "", supplier_credit_note_number: "",
        credit_note_date: new Date().toISOString().slice(0, 10),
        reason: "", currency_code: "INR", subtotal_minor: "", tax_adjustment_minor: "0", notes: "",
      });
      loadCNs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create credit note");
    } finally {
      setSubmitting(false);
    }
  };

  // Submit CN for approval
  const submitForApproval = async (cnId) => {
    try {
      await axios.post(`${API}/api/ap/credit-notes/${cnId}/submit`, {}, { withCredentials: true });
      toast.success("Credit note submitted for approval");
      loadCNs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    }
  };

  // Approve CN
  const confirmApprove = async (e) => {
    e.preventDefault();
    const amt = parseInt(approveAmt, 10);
    if (!amt || amt <= 0) return toast.error("Approved amount must be positive");
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/ap/credit-notes/${approveCN.id}/approve`, {
        approved_amount_minor: amt,
      }, { withCredentials: true });
      toast.success("Credit note approved");
      setApproveCN(null);
      setApproveAmt("");
      loadCNs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approval failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Reject CN
  const rejectCN = async (cnId) => {
    const reason = window.prompt("Rejection reason (required):");
    if (!reason) return;
    try {
      await axios.post(`${API}/api/ap/credit-notes/${cnId}/reject`, { reason }, { withCredentials: true });
      toast.success("Credit note rejected");
      loadCNs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rejection failed");
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div data-testid="finance-credit-notes" style={{ minHeight: "100vh", background: "#F8F7F4" }}>

      {/* Header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 30,
        background: "rgba(248,247,244,0.96)", backdropFilter: "blur(12px)",
        borderBottom: "1px solid #E7D8C0", padding: "16px 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px",
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#1C160C" }}>
            Supplier Credit Notes
          </h1>
          <p style={{ margin: 0, fontSize: "12px", color: "#9E8A6B" }}>
            AP-PAY-01C · Credit notes against approved invoices
          </p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button data-testid="refresh-cn-btn" onClick={loadCNs}
            style={ghostBtnStyle}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          {canValidate && (
            <button data-testid="create-cn-btn" onClick={() => setShowCreate(true)}
              style={primaryBtnStyle}>
              <Plus size={14} /> New Credit Note
            </button>
          )}
          <button onClick={() => navigate("/ap/payments")} style={ghostBtnStyle}>
            ← Payments
          </button>
        </div>
      </div>

      <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>

        {/* Filters */}
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "20px" }}>
          {["ALL", ...ALL_STATUSES].map((st) => (
            <button key={st} data-testid={`cn-filter-${st}`}
              onClick={() => { setActiveFilter(st); setPage(1); }}
              style={{
                padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: 600, cursor: "pointer",
                background: activeFilter === st ? "#1C160C" : "transparent",
                color: activeFilter === st ? "#FFF" : "#6B5B4A",
                border: activeFilter === st ? "none" : "1px solid #D4C5A9",
              }}>
              {st}
            </button>
          ))}
          <span style={{ marginLeft: "auto", fontSize: "12px", color: "#9E8A6B", alignSelf: "center" }}>
            {total} credit note{total !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#9E8A6B" }}>Loading…</div>
        ) : items.length === 0 ? (
          <div data-testid="cn-empty-state" style={{
            textAlign: "center", padding: "80px 0",
            background: "#FFF", borderRadius: "16px", border: "1px solid #E7D8C0",
          }}>
            <FileText size={40} style={{ color: "#D4C5A9", marginBottom: "12px" }} />
            <p style={{ color: "#9E8A6B", fontSize: "15px", margin: 0 }}>No credit notes yet</p>
          </div>
        ) : (
          <div style={{ background: "#FFF", borderRadius: "16px", border: "1px solid #E7D8C0", overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ background: "#FDFAF5", borderBottom: "1px solid #E7D8C0" }}>
                    {["CN Ref", "Original Invoice", "Supplier", "Date", "Total", "Approved", "Status", "Actions"].map(h => (
                      <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600,
                        color: "#9E8A6B", fontSize: "11px", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((cn, idx) => (
                    <tr key={cn.id} data-testid={`cn-row-${idx}`}
                      style={{ borderBottom: "1px solid #F3EDE3" }}>
                      <td style={{ padding: "12px 16px", fontFamily: "monospace", fontSize: "12px", color: "#1C160C", fontWeight: 700 }}>
                        {cn.ap_cn_reference || cn.id?.slice(-8)}
                      </td>
                      <td style={{ padding: "12px 16px", fontFamily: "monospace", fontSize: "12px", color: "#6B5B4A" }}>
                        {cn.original_ap_reference || "—"}
                      </td>
                      <td style={{ padding: "12px 16px", color: "#3A2E1E" }}>
                        {cn.supplier_name || cn.supplier_id}
                      </td>
                      <td style={{ padding: "12px 16px", color: "#9E8A6B", whiteSpace: "nowrap" }}>
                        {fmtDate(cn.credit_note_date)}
                      </td>
                      <td style={{ padding: "12px 16px", fontWeight: 600, color: "#1C160C" }}>
                        {fmt(cn.total_minor, cn.currency_code)}
                      </td>
                      <td style={{ padding: "12px 16px", color: cn.approved_amount_minor ? "#166534" : "#9E8A6B" }}>
                        {fmt(cn.approved_amount_minor, cn.currency_code)}
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <CNStatusBadge status={cn.status} />
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ display: "flex", gap: "6px" }}>
                          {cn.status === "DRAFT" && canValidate && (
                            <button data-testid={`cn-submit-btn-${idx}`}
                              onClick={() => submitForApproval(cn.id)}
                              style={microBtn("#1D4ED8")}>Submit</button>
                          )}
                          {cn.status === "SUBMITTED" && canApprove && (
                            <>
                              <button data-testid={`cn-approve-btn-${idx}`}
                                onClick={() => { setApproveCN(cn); setApproveAmt(String(cn.total_minor)); }}
                                style={microBtn("#166534")}>Approve</button>
                              <button data-testid={`cn-reject-btn-${idx}`}
                                onClick={() => rejectCN(cn.id)}
                                style={microBtn("#991B1B")}>Reject</button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div style={{ padding: "12px 16px", borderTop: "1px solid #E7D8C0",
                display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: "12px", color: "#9E8A6B" }}>Page {page} of {totalPages}</span>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    style={{ padding: "5px 10px", fontSize: "12px", borderRadius: "6px", border: "1px solid #D4C5A9",
                      background: "transparent", cursor: page === 1 ? "default" : "pointer",
                      color: page === 1 ? "#D4C5A9" : "#6B5B4A" }}>Prev</button>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    style={{ padding: "5px 10px", fontSize: "12px", borderRadius: "6px", border: "1px solid #D4C5A9",
                      background: "transparent", cursor: page === totalPages ? "default" : "pointer",
                      color: page === totalPages ? "#D4C5A9" : "#6B5B4A" }}>Next</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Create CN Modal ─────────────────────────────────────────────── */}
      {showCreate && (
        <div data-testid="create-cn-modal"
          style={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(4px)", display: "flex", alignItems: "center",
            justifyContent: "center", padding: "20px" }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowCreate(false); }}>
          <div style={{ background: "#FFF", borderRadius: "20px", width: "100%", maxWidth: "540px",
            padding: "28px", boxShadow: "0 24px 80px rgba(0,0,0,0.2)", maxHeight: "90vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: 700, color: "#1C160C" }}>
              New Credit Note
            </h2>
            <p style={{ margin: "0 0 20px", fontSize: "12px", color: "#9E8A6B" }}>
              AP-PAY-01C · Original invoice is not modified. Credit note reduces outstanding liability on approval.
            </p>
            <form onSubmit={submitCreate} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <div>
                <label style={lbl}>Original Invoice <span style={{ color: "#BA0517" }}>*</span></label>
                {approvedInvoices.length > 0 ? (
                  <select data-testid="cn-invoice-select"
                    value={createForm.original_invoice_id}
                    onChange={(e) => setCreateForm(f => ({ ...f, original_invoice_id: e.target.value }))}
                    style={inp} required>
                    <option value="">Select approved invoice…</option>
                    {approvedInvoices.map(inv => (
                      <option key={inv.id} value={inv.id}>
                        {inv.ap_reference} — {fmt(inv.total_minor, inv.currency_code)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input data-testid="cn-invoice-input"
                    value={createForm.original_invoice_id}
                    onChange={(e) => setCreateForm(f => ({ ...f, original_invoice_id: e.target.value }))}
                    placeholder="Approved Invoice ID" style={inp} required />
                )}
              </div>
              <div>
                <label style={lbl}>Supplier CN Number <span style={{ color: "#BA0517" }}>*</span></label>
                <input data-testid="cn-number-input"
                  value={createForm.supplier_credit_note_number}
                  onChange={(e) => setCreateForm(f => ({ ...f, supplier_credit_note_number: e.target.value }))}
                  placeholder="Supplier's credit note number" style={inp} required />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={lbl}>CN Date <span style={{ color: "#BA0517" }}>*</span></label>
                  <input type="date" data-testid="cn-date-input"
                    value={createForm.credit_note_date}
                    onChange={(e) => setCreateForm(f => ({ ...f, credit_note_date: e.target.value }))}
                    style={inp} required />
                </div>
                <div>
                  <label style={lbl}>Currency</label>
                  <select data-testid="cn-currency-select"
                    value={createForm.currency_code}
                    onChange={(e) => setCreateForm(f => ({ ...f, currency_code: e.target.value }))}
                    style={inp}>
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={lbl}>Reason <span style={{ color: "#BA0517" }}>*</span></label>
                <input data-testid="cn-reason-input"
                  value={createForm.reason}
                  onChange={(e) => setCreateForm(f => ({ ...f, reason: e.target.value }))}
                  placeholder="Reason for credit note" style={inp} required />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={lbl}>Subtotal (minor) <span style={{ color: "#BA0517" }}>*</span></label>
                  <input type="number" min="1" step="1" data-testid="cn-subtotal-input"
                    value={createForm.subtotal_minor}
                    onChange={(e) => setCreateForm(f => ({ ...f, subtotal_minor: e.target.value }))}
                    style={inp} required />
                </div>
                <div>
                  <label style={lbl}>Tax Adjustment (minor)</label>
                  <input type="number" step="1" data-testid="cn-tax-input"
                    value={createForm.tax_adjustment_minor}
                    onChange={(e) => setCreateForm(f => ({ ...f, tax_adjustment_minor: e.target.value }))}
                    style={inp} />
                </div>
              </div>
              <div style={{ paddingTop: "10px", borderTop: "1px solid #F3EDE3",
                display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button type="button" onClick={() => setShowCreate(false)}
                  style={{ padding: "9px 18px", borderRadius: "8px", border: "1px solid #D4C5A9",
                    background: "transparent", color: "#6B5B4A", cursor: "pointer", fontSize: "13px" }}>
                  Cancel
                </button>
                <button type="submit" disabled={submitting} data-testid="cn-create-submit-btn"
                  style={{ padding: "9px 20px", borderRadius: "8px", border: "none",
                    background: submitting ? "#9E8A6B" : "#1C160C", color: "#FFF",
                    fontWeight: 600, fontSize: "13px", cursor: submitting ? "default" : "pointer" }}>
                  {submitting ? "Creating…" : "Create Credit Note"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Approve CN Modal ─────────────────────────────────────────────── */}
      {approveCN && (
        <div data-testid="approve-cn-modal"
          style={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(4px)", display: "flex", alignItems: "center",
            justifyContent: "center", padding: "20px" }}
          onClick={(e) => { if (e.target === e.currentTarget) setApproveCN(null); }}>
          <div style={{ background: "#FFF", borderRadius: "20px", width: "100%", maxWidth: "440px",
            padding: "28px", boxShadow: "0 24px 80px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: "0 0 8px", fontSize: "17px", fontWeight: 700, color: "#1C160C" }}>Approve Credit Note</h2>
            <p style={{ margin: "0 0 16px", fontSize: "12px", color: "#9E8A6B" }}>
              {approveCN.ap_cn_reference} · CN Total: {fmt(approveCN.total_minor, approveCN.currency_code)}
            </p>
            <form onSubmit={confirmApprove} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <div>
                <label style={lbl}>Approved Amount (minor) <span style={{ color: "#BA0517" }}>*</span></label>
                <input type="number" min="1" step="1" data-testid="cn-approve-amount-input"
                  value={approveAmt}
                  onChange={(e) => setApproveAmt(e.target.value)}
                  style={inp} required />
              </div>
              <div style={{ paddingTop: "10px", borderTop: "1px solid #F3EDE3",
                display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button type="button" onClick={() => setApproveCN(null)}
                  style={{ padding: "9px 18px", borderRadius: "8px", border: "1px solid #D4C5A9",
                    background: "transparent", color: "#6B5B4A", cursor: "pointer", fontSize: "13px" }}>
                  Cancel
                </button>
                <button type="submit" disabled={submitting} data-testid="cn-approve-submit-btn"
                  style={{ padding: "9px 20px", borderRadius: "8px", border: "none",
                    background: submitting ? "#9E8A6B" : "#166534", color: "#FFF",
                    fontWeight: 600, fontSize: "13px", cursor: submitting ? "default" : "pointer" }}>
                  {submitting ? "Approving…" : "Approve"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const ghostBtnStyle = {
  display: "flex", alignItems: "center", gap: "6px",
  padding: "8px 14px", background: "transparent", border: "1px solid #D4C5A9",
  borderRadius: "8px", fontSize: "13px", color: "#6B5B4A", cursor: "pointer",
};
const primaryBtnStyle = {
  display: "flex", alignItems: "center", gap: "6px",
  padding: "8px 16px", background: "#1C160C", border: "none",
  borderRadius: "8px", fontSize: "13px", fontWeight: 600, color: "#FFF", cursor: "pointer",
};
const lbl = {
  display: "block", marginBottom: "5px", fontSize: "12px", fontWeight: 600, color: "#6B5B4A",
};
const inp = {
  width: "100%", padding: "9px 12px", boxSizing: "border-box",
  border: "1px solid #D4C5A9", borderRadius: "8px",
  fontSize: "13px", color: "#1C160C", background: "#FDFAF5", outline: "none",
};
function microBtn(color) {
  return {
    padding: "4px 10px", borderRadius: "6px", border: "none",
    background: color, color: "#FFF", fontSize: "11px",
    fontWeight: 600, cursor: "pointer",
  };
}
